/** Shared analytics data store; refetches only when date range, granularity or environment changes. */

import { AggregatedSessionV2, GroupData } from "@global/types/session";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { apiClient } from "../api";
import { Environment } from "../config";
import { useFilters } from "./filters-context";

// ============================================================================
// TYPES
// ============================================================================

interface DataStoreState {
  // Session data (used by Overview, Sessions, Pages, Journeys, Acquisition)
  sessions: AggregatedSessionV2[];
  sessionsLoading: boolean;
  sessionsError: string | null;

  // Aggregated data (used by Acquisition)
  aggregatedData: Record<string, GroupData>;
  aggregatedLoading: boolean;
  aggregatedError: string | null;

  // Last fetch params for cache invalidation
  lastFetchParams: {
    startDate: string;
    endDate: string;
    granularity: string;
    environment: Environment;
  } | null;
}

interface DataStoreContextValue extends DataStoreState {
  refetch: () => Promise<void>;
  setEnvironment: (env: Environment) => void;
  environment: Environment;
  deleteSession: (sessionId: string) => Promise<void>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const DataStoreContext = createContext<DataStoreContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface DataStoreProviderProps {
  children: React.ReactNode;
}

export const DataStoreProvider: React.FC<DataStoreProviderProps> = ({
  children,
}) => {
  const { filters } = useFilters();

  // Environment state
  const [environment, setEnvironmentState] = useState<Environment>(
    apiClient.getEnvironment()
  );

  // Session data state
  const [sessions, setSessions] = useState<AggregatedSessionV2[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  // Aggregated data state
  const [aggregatedData, setAggregatedData] = useState<
    Record<string, GroupData>
  >({});
  const [aggregatedLoading, setAggregatedLoading] = useState(true);
  const [aggregatedError, setAggregatedError] = useState<string | null>(null);

  // Last fetch params
  const [lastFetchParams, setLastFetchParams] =
    useState<DataStoreState["lastFetchParams"]>(null);

  // Ref to track if we're currently fetching to avoid double-fetches
  const fetchingRef = useRef(false);

  const shouldRefetch = useCallback((): boolean => {
    if (!lastFetchParams) return true;

    const currentParams = {
      startDate: filters.dateRange.start,
      endDate: filters.dateRange.end,
      granularity: filters.granularity,
      environment,
    };

    return (
      currentParams.startDate !== lastFetchParams.startDate ||
      currentParams.endDate !== lastFetchParams.endDate ||
      currentParams.granularity !== lastFetchParams.granularity ||
      currentParams.environment !== lastFetchParams.environment
    );
  }, [filters, environment, lastFetchParams]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    // Prevent concurrent fetches
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    setSessionsLoading(true);
    setAggregatedLoading(true);
    setSessionsError(null);
    setAggregatedError(null);

    const currentParams = {
      startDate: filters.dateRange.start,
      endDate: filters.dateRange.end,
      granularity: filters.granularity,
      environment,
    };

    try {
      const [sessionsResult, aggregatedResult] = await Promise.all([
        apiClient.getSessions({
          startDate: filters.dateRange.start,
          endDate: filters.dateRange.end,
        }),
        apiClient.getData({
          startDate: filters.dateRange.start,
          endDate: filters.dateRange.end,
          granularity:
            filters.granularity === "hour" ? "day" : filters.granularity,
        }),
      ]);

      setSessions(sessionsResult.sessions);
      setAggregatedData(aggregatedResult);
      setLastFetchParams(currentParams);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load data";
      setSessionsError(errorMessage);
      setAggregatedError(errorMessage);
    } finally {
      setSessionsLoading(false);
      setAggregatedLoading(false);
      fetchingRef.current = false;
    }
  }, [filters, environment]);

  // Manual refetch function
  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Environment setter that also updates the API client
  const setEnvironment = useCallback((env: Environment) => {
    apiClient.setEnvironment(env);
    setEnvironmentState(env);
  }, []);

  const deleteSession = useCallback(async (sessionId: string) => {
    await apiClient.deleteSession(sessionId);
    setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
  }, []);

  // Auto-fetch when dependencies change
  useEffect(() => {
    if (shouldRefetch()) {
      fetchData();
    }
  }, [
    filters.dateRange.start,
    filters.dateRange.end,
    filters.granularity,
    environment,
    shouldRefetch,
    fetchData,
  ]);

  const value: DataStoreContextValue = {
    sessions,
    sessionsLoading,
    sessionsError,
    aggregatedData,
    aggregatedLoading,
    aggregatedError,
    lastFetchParams,
    refetch,
    setEnvironment,
    environment,
    deleteSession,
  };

  return (
    <DataStoreContext.Provider value={value}>
      {children}
    </DataStoreContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

export function useDataStore(): DataStoreContextValue {
  const context = useContext(DataStoreContext);
  if (!context) {
    throw new Error("useDataStore must be used within a DataStoreProvider");
  }
  return context;
}
